import { useMemo, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Alert, AlertDescription } from 'src/@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'src/@/components/ui/accordion';
import { Input } from 'src/@/components/ui/input';
import { Label } from 'src/@/components/ui/label';
import { Switch } from 'src/@/components/ui/switch';
import { Separator } from 'src/@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/@/components/ui/select';

const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

const PARCEIROS = [
    {
        value: 'imoview',
        label: 'Imoview',
        descricao: 'CRM da Universal Software',
        ondeObterToken: 'No Imoview, abra o menu superior direito → "Detalhes do convênio" → copie a "Chave de API".',
    },
];

export default function EnvioCrmParceiroSection({ aiAgent, changeModuleConfig }) {
    const realstate = aiAgent?.config?.module?.realstate || {};
    const funcoes = realstate.funcoes || {};
    const cfg = funcoes.envioCrmParceiro || {};
    const workflow = realstate.perguntasEssenciais?.workflow || { nodes: [] };

    const totalQualificatorias = useMemo(() => {
        const perguntas = (workflow.nodes || []).flatMap((n) => n.data?.perguntas || []);
        return perguntas.filter((p) => p?.qualificatoria).length;
    }, [workflow.nodes]);

    const [revelarToken, setRevelarToken] = useState(false);

    const updateCfg = (patch) => {
        changeModuleConfig({
            funcoes: {
                ...funcoes,
                envioCrmParceiro: { ...cfg, ...patch },
            },
        });
    };

    const parceiroSelecionado = PARCEIROS.find((p) => p.value === cfg.parceiro);
    const ativoSemParceiro = !!cfg.ativo && !cfg.parceiro;
    const ativoSemToken = !!cfg.ativo && !!cfg.parceiro && !cfg.token;

    return (
        <Accordion
            type="single"
            collapsible
            className="rounded-xl border border-gray-100 bg-white"
        >
            <AccordionItem value="envio-crm" className="border-0">
                <AccordionTrigger className="hover:no-underline px-6 py-4 [&[data-state=open]]:pb-3">
                    <div className="flex items-start justify-between gap-3 flex-1 pr-3 text-left">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50 shrink-0">
                                <Iconify icon="mdi:database-export-outline" width={20} className="text-violet-600" />
                            </div>
                            <div>
                                <div className="text-base font-semibold text-gray-900 leading-tight">Envio do Lead ao CRM Parceiro</div>
                                <div className="text-sm text-gray-500 font-normal mt-1">
                                    Quando o lead atingir o número configurado de perguntas qualificatórias respondidas,
                                    a Lais envia o lead ao CRM da imobiliária parceira — uma única vez.
                                </div>
                            </div>
                        </div>
                        <div
                            role="presentation"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="shrink-0"
                        >
                            <Switch
                                checked={!!cfg.ativo}
                                onCheckedChange={(value) => updateCfg({ ativo: value })}
                            />
                        </div>
                    </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                    {!cfg.ativo ? (
                        <p className="text-xs text-gray-500 italic">Ative o recurso acima para configurar.</p>
                    ) : (
                <div className="flex flex-col gap-4">
                        {ativoSemParceiro && (
                            <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                                <AlertDescription className="text-xs">
                                    Selecione um parceiro para a integração começar a funcionar.
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="flex flex-col gap-2 max-w-md">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Parceiro</Label>
                            <Select
                                value={cfg.parceiro || ''}
                                onValueChange={(value) => updateCfg({ parceiro: value })}
                            >
                                <SelectTrigger className={INPUT_CLS}>
                                    <SelectValue placeholder="Selecione um parceiro..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {PARCEIROS.map((p) => (
                                        <SelectItem key={p.value} value={p.value}>
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium">{p.label}</span>
                                                <span className="text-xs text-gray-500">{p.descricao}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-gray-500">Mais parceiros serão adicionados em breve.</p>
                        </div>

                        {parceiroSelecionado && (
                            <>
                                <Alert className="bg-blue-50 border-blue-200 text-blue-800">
                                    <AlertDescription className="text-xs">
                                        {parceiroSelecionado.ondeObterToken}
                                    </AlertDescription>
                                </Alert>

                                <div className="flex flex-col gap-2 max-w-md">
                                    <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                        Token (Chave de API)
                                    </Label>
                                    <div className="relative">
                                        <Input
                                            type={revelarToken ? 'text' : 'password'}
                                            placeholder="Cole aqui a chave do convênio"
                                            value={cfg.token || ''}
                                            onChange={(e) => updateCfg({ token: e.target.value })}
                                            className={`${INPUT_CLS} pr-10 ${ativoSemToken ? 'border-amber-300' : ''}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setRevelarToken((v) => !v)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                            tabIndex={-1}
                                        >
                                            <Iconify icon={revelarToken ? 'eva:eye-off-outline' : 'eva:eye-outline'} width={16} />
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}

                        <Separator className="bg-gray-200/60" />

                        <div className="flex flex-col gap-2 max-w-xs">
                            <Label className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                Threshold (qualificatórias respondidas)
                            </Label>
                            <Input
                                type="number"
                                min={1}
                                max={totalQualificatorias || undefined}
                                placeholder={totalQualificatorias > 0 ? `Padrão: ${totalQualificatorias} (todas)` : 'Padrão: todas'}
                                value={cfg.threshold ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    if (v === '') return updateCfg({ threshold: null });
                                    const n = parseInt(v, 10);
                                    if (Number.isFinite(n)) updateCfg({ threshold: Math.max(1, n) });
                                }}
                                className={INPUT_CLS}
                            />
                            <p className="text-xs text-gray-500">
                                Quantas perguntas qualificatórias precisam estar respondidas antes de enviar.
                                {totalQualificatorias > 0
                                    ? ` Em branco usa o total atual do workflow (${totalQualificatorias}).`
                                    : ' Marque perguntas como qualificatórias na aba "Perguntas Essenciais" antes de configurar.'}
                            </p>
                        </div>

                        <Alert className="bg-violet-50 border-violet-200 text-violet-800">
                            <AlertDescription className="text-xs">
                                <strong>Idempotente:</strong> o lead é enviado uma única vez. Em caso de falha de rede/HTTP,
                                a Lais tenta de novo na próxima atualização do lead. Status e resposta do parceiro ficam
                                registrados no histórico do lead para diagnóstico.
                            </AlertDescription>
                        </Alert>
                </div>
                    )}
                </AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}
